import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, Heart } from "lucide-react";
import { useAuth } from "../lib/auth";

const Login = () => {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await login(email.trim(), password); nav("/dashboard"); }
    catch (e) { setErr(e?.response?.data?.detail || "Login failed. Check credentials."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-[100dvh] bg-[#FFF7EE] px-6 pt-12 pb-8 flex flex-col">
      <div className="mb-8">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F4844B] to-[#EF4444] flex items-center justify-center shadow-lg shadow-[#F4844B]/30 mb-6">
          <Heart size={26} className="text-white" fill="white" />
        </div>
        <h1 className="font-instrument text-3xl font-semibold text-[#0A4C36] leading-tight">Welcome back</h1>
        <p className="text-[#57534E] mt-2">Sign in to your Health Circles account.</p>
      </div>

      <form onSubmit={submit} className="flex-1 flex flex-col gap-4">
        <label className="block">
          <span className="text-xs font-medium text-[#57534E] ml-1">Email</span>
          <div className="relative mt-1">
            <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
            <input
              data-testid="login-email"
              type="email" required autoComplete="email"
              className="input-field pl-11" placeholder="you@example.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-[#57534E] ml-1">Password</span>
          <div className="relative mt-1">
            <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
            <input
              data-testid="login-password"
              type={show ? "text" : "password"} required autoComplete="current-password"
              className="input-field pl-11 pr-11" placeholder="••••••••"
              value={password} onChange={(e) => setPassword(e.target.value)}
            />
            <button type="button" onClick={() => setShow((s) => !s)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A8A29E]">
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>

        {err && <div data-testid="login-error" className="text-sm text-red-500 bg-red-50 rounded-xl px-4 py-2.5">{err}</div>}

        <div className="mt-2">
          <button data-testid="login-submit" type="submit" disabled={busy} className="btn-primary">
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </div>

        <Link to="/awareness" data-testid="login-explore" className="text-center text-sm text-[#57534E] mt-2 no-underline">
          Just browsing? <span className="text-[#F4844B] font-medium">Explore awareness →</span>
        </Link>
      </form>

      <p className="text-center text-sm text-[#57534E] mt-6">
        New here?{" "}
        <Link to="/signup" data-testid="login-to-signup" className="text-[#0A4C36] font-semibold">Create account</Link>
      </p>
    </div>
  );
};

export default Login;
